import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getReaderChapterVoice, ReaderBook, ReaderChapter } from '@/services/booksApi';
import { OFFLINE_BOUNDARY_MESSAGE, useOfflineBoundary } from '@/hooks/useOfflineBoundary';
import { useChapterFragments } from '@/hooks/useChapterFragments';
import { useEndnotesIndex } from '@/hooks/useEndnotesIndex';
import { resolveWordSyncTimingIndex, useWordSync } from '@/hooks/useWordSync';
import { resolveChapterRoot } from '@/utils/chapterDom';
import ReaderShadowHost from '@/components/ReaderShadowHost';
import EndnotePopup, { EndnotePopupStatus } from '@/components/EndnotePopup';
import { useTheme } from '@/contexts/ThemeContext';
import { ChapterTimingsStatus } from '@/hooks/useChapterAudio';
import { fetchChapterRuntime } from '@/hooks/useChapterAudio';
import { ChapterRuntime } from '@/utils/ordinalSync';
import { getMountedChapterIndex } from '@/utils/readerDomRegistry';
import ConversationMarginDots from '@/components/ConversationMarginDots';
import { ChatSessionSummary } from '@/types/chat';
import type { AnalyticsInputMethod } from '@/services/analyticsContext';
import { resolveReaderLink } from '@/utils/readerFragments';
import { stripBacklink } from '@/utils/endnoteContent';

const ENDNOTES_BASENAME = 'endnotes';
const NO_SESSIONS: ChatSessionSummary[] = [];
const NO_CHAPTERS: ReaderChapter[] = [];
const ENDNOTE_POPUP_TITLE_ID = 'reader-endnote-popup-title';

interface EndnotePopupState {
  noteId: string;
  noteNumber: string | null;
  citingChapterId: string;
  anchorRect: DOMRect;
  status: EndnotePopupStatus;
  noteElement: HTMLElement | null;
}
import type {
  ReaderContentReadiness,
  ReaderChapterSelectionRequest,
  ReaderPositionAcknowledgement,
  ReaderPositionRequest,
} from '@/types/readerReadiness';

const LINE_KEY_PRECISION_PX = 0.5;
const MAX_MIDPOINT_DEAD_ZONE_PX = 12;
const MIN_SCROLL_DELTA_PX = 0.5;
const MANUAL_SCROLL_INTENT_TIMEOUT_MS = 250;
const AUTOMATIC_SCROLL_GRACE_MS = 600;
const MAX_RETURN_SMOOTH_DISTANCE_VIEWPORTS = 10;
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End']);

interface FollowScrollOptions {
  behavior?: ScrollBehavior;
  forceFollow?: boolean;
  maxSmoothDistanceViewports?: number;
}

const roundToPrecision = (value: number, precision: number) =>
  Math.round(value / precision) * precision;

const getLineKey = (top: number, height: number) =>
  `${roundToPrecision(top, LINE_KEY_PRECISION_PX)}:${roundToPrecision(height, LINE_KEY_PRECISION_PX)}`;

const findFragmentTarget = (root: HTMLElement, fragmentId: string | null): HTMLElement | null => {
  if (!fragmentId) {
    return root;
  }
  if (root.id === fragmentId) {
    return root;
  }
  const escaped = typeof CSS !== 'undefined' && CSS.escape
    ? CSS.escape(fragmentId)
    : fragmentId.replace(/"/g, '\\"');
  return root.querySelector<HTMLElement>(`#${escaped},[name="${escaped}"]`);
};

const revealNavigationTarget = (
  root: HTMLElement,
  fragmentId: string | null,
  scrollContainer: HTMLElement | null,
  behaviorOverride?: ScrollBehavior,
): HTMLElement | null => {
  const target = findFragmentTarget(root, fragmentId);
  if (!target) {
    return null;
  }
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const behavior: ScrollBehavior = behaviorOverride ?? (reduceMotion ? 'auto' : 'smooth');
  if (!scrollContainer || scrollContainer.clientHeight <= 0) {
    target.scrollIntoView({ block: 'center', behavior });
    return target;
  }
  const targetRect = target.getBoundingClientRect();
  const scrollRect = scrollContainer.getBoundingClientRect();
  const targetTop = scrollContainer.scrollTop + targetRect.top - scrollRect.top;
  const desiredTop = Math.max(
    0,
    Math.min(
      targetTop + (targetRect.height / 2) - (scrollContainer.clientHeight / 2),
      scrollContainer.scrollHeight - scrollContainer.clientHeight,
    ),
  );
  scrollContainer.scrollTo({ top: desiredTop, behavior });
  return target;
};

const centerChapterOpening = (
  readerRoot: HTMLElement,
  scrollContainer: HTMLElement,
  chapterId: string,
  alignTop = false,
): boolean => {
  const chapterRoot = resolveChapterRoot(readerRoot, chapterId);
  if (!chapterRoot) return false;
  const target = chapterRoot.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6') ?? chapterRoot;
  if (scrollContainer.clientHeight <= 0) {
    target.scrollIntoView({ block: alignTop ? 'start' : 'center', behavior: 'instant' });
    return true;
  }
  const targetRect = target.getBoundingClientRect();
  const scrollRect = scrollContainer.getBoundingClientRect();
  const targetTop = scrollContainer.scrollTop + targetRect.top - scrollRect.top;
  const rawDesiredTop = alignTop ? targetTop : targetTop + (targetRect.height / 2) - (scrollContainer.clientHeight / 2);
  const desiredTop = Math.max(
    0,
    Math.min(rawDesiredTop, scrollContainer.scrollHeight - scrollContainer.clientHeight),
  );
  if (Math.abs(scrollContainer.scrollTop - desiredTop) > 0.5) {
    scrollContainer.scrollTo({ top: desiredTop, behavior: 'instant' });
  }
  return true;
};

interface TranscribePlaybackProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  currentChapter: ReaderChapter | null;
  readerBook: ReaderBook | null;
  isRenderProfileSupported: boolean;
  runtime: ChapterRuntime | null;
  chapterTimingsStatus: ChapterTimingsStatus;
  selectedReadingVoiceKey: string;
  /** A voice conversation is live (or connecting): tap-to-seek is suppressed. */
  isVoiceSessionActive: boolean;
  onSeekAndPlay: (time: number, targetChapterId?: string) => void;
  onNavigateChapter: (chapterId: string, options?: { fragmentId?: string | null }) => void;
  isOpen: boolean;
  chapterSelectionRequest: ReaderChapterSelectionRequest | null;
  chapterRecoveryNotice: string | null;
  positionRequest: ReaderPositionRequest | null;
  /**
   * Saved chapter-local offset the audio element has not been given yet, or
   * null when there is nothing left to restore. Seeds the first positioning
   * pass so a page load lands on the saved line instead of the chapter start.
   */
  pendingResumeSeconds: number | null;
  onReaderContentReadinessChange: (readiness: ReaderContentReadiness | null) => void;
  onReaderPositioned: (acknowledgement: ReaderPositionAcknowledgement | null) => void;
  /**
   * The book's anchored conversations, shown as dots in the right margin.
   * The dots and their gutter exist only when onOpenConversation is given.
   */
  conversationSessions?: readonly ChatSessionSummary[];
  /** The conversation the text-chat sheet is showing; its dot gets the halo. */
  activeConversationId?: number | null;
  onOpenConversation?: (session: ChatSessionSummary, inputMethod: AnalyticsInputMethod) => void;
}

const TranscribePlaybackComponent: React.FC<TranscribePlaybackProps> = ({
  audioRef,
  currentChapter,
  readerBook,
  isRenderProfileSupported,
  runtime,
  chapterTimingsStatus,
  selectedReadingVoiceKey,
  isVoiceSessionActive,
  onSeekAndPlay,
  onNavigateChapter,
  isOpen,
  chapterSelectionRequest,
  chapterRecoveryNotice,
  positionRequest,
  pendingResumeSeconds,
  onReaderContentReadinessChange,
  onReaderPositioned,
  conversationSessions = NO_SESSIONS,
  activeConversationId = null,
  onOpenConversation,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const suppressScrollRef = useRef<boolean>(false);
  const latestTokenClickRequestRef = useRef<number>(0);
  const inFlightTokenFetchAbortRef = useRef<AbortController | null>(null);
  // Read inside the shadow-host click listener, which is registered once per
  // chapter render -- a ref keeps the live value without re-registering.
  const isVoiceSessionActiveRef = useRef<boolean>(isVoiceSessionActive);
  const pointerGestureRef = useRef<{ pointerId: number; x: number; y: number; moved: boolean } | null>(null);
  const navigationFocusCleanupRef = useRef<(() => void) | null>(null);
  const previousChapterIdRef = useRef<string | null>(null);
  const chapterScrollOffsetsRef = useRef<Map<string, number>>(new Map());
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const readerRootRef = useRef<HTMLDivElement | null>(null);
  const popupRootRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const readerWrapperRef = useRef<HTMLDivElement | null>(null);
  const latestNoteRequestRef = useRef<number>(0);
  const openEndnoteAnchorRef = useRef<HTMLAnchorElement | null>(null);
  const hasStartedMidpointFollowRef = useRef<boolean>(false);
  const lastFollowTimingIndexRef = useRef<number>(-1);
  const lastFollowLineKeyRef = useRef<string | null>(null);
  const lastFollowTargetTopRef = useRef<number | null>(null);
  const isFollowSuspendedRef = useRef<boolean>(false);
  const automaticScrollUntilRef = useRef<number>(0);
  const pendingInstantChapterIdRef = useRef<string | null>(null);
  const holdExplicitNavigationPositionRef = useRef(false);
  const handledChapterSelectionRequestRef = useRef<number>(0);
  const pendingChapterSelectionRequestRef = useRef<ReaderChapterSelectionRequest | null>(null);
  const selectionRevealAnimationFrameRef = useRef<number | null>(null);
  const acknowledgedPositionRequestRef = useRef<number>(0);
  const previousIsOpenRef = useRef<boolean>(false);
  const isOpenRef = useRef<boolean>(isOpen);
  isOpenRef.current = isOpen;
  const syncAnimationFrameRef = useRef<number | null>(null);
  const scrollMetricsRef = useRef<{
    chapterTopInScroll: number;
    viewportHeight: number;
    maxScrollTop: number;
  } | null>(null);
  const isInitialPositionCompleteRef = useRef<boolean>(false);
  // Mirrors the selected chapter for callbacks that must not be re-created
  // when it changes (markInitialPositionComplete is a dependency of the
  // positioning effects).
  const currentChapterIdRef = useRef<string | null>(null);
  currentChapterIdRef.current = currentChapter?.id ?? null;
  // The chapter whose view has already been placed, by any positioning pass.
  // The coarse pass below triggers on `htmlVersion`, which also advances for
  // same-chapter remounts, and must not scroll over an established position.
  const positionedChapterIdRef = useRef<string | null>(null);
  // Read by syncToAudioTime through a ref so its identity doesn't change when
  // the restore lands — it is a dependency of several positioning effects.
  const pendingResumeSecondsRef = useRef<number | null>(pendingResumeSeconds);
  pendingResumeSecondsRef.current = pendingResumeSeconds;
  const [shadowReadyVersion, setShadowReadyVersion] = useState(0);
  const [isFollowSuspended, setIsFollowSuspended] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [isInitialPositionComplete, setIsInitialPositionComplete] = useState(false);
  const [navigationNotice, setNavigationNotice] = useState<string | null>(null);
  const [endnotePopup, setEndnotePopup] = useState<EndnotePopupState | null>(null);
  const endnotesIndex = useEndnotesIndex(readerBook);

  const focusExplicitNavigationTarget = useCallback((target: HTMLElement) => {
    navigationFocusCleanupRef.current?.();
    const hadTabIndex = target.hasAttribute('tabindex');
    if (!hadTabIndex) target.tabIndex = -1;
    target.dataset.readerNavigationTarget = 'true';

    let cleanupTimeoutId: number | null = null;
    const cleanup = () => {
      if (cleanupTimeoutId !== null) {
        window.clearTimeout(cleanupTimeoutId);
        cleanupTimeoutId = null;
      }
      target.removeEventListener('blur', cleanup);
      delete target.dataset.readerNavigationTarget;
      if (!hadTabIndex) target.removeAttribute('tabindex');
      if (navigationFocusCleanupRef.current === cleanup) {
        navigationFocusCleanupRef.current = null;
      }
    };

    navigationFocusCleanupRef.current = cleanup;
    target.addEventListener('blur', cleanup, { once: true });
    cleanupTimeoutId = window.setTimeout(cleanup, 1000);
    target.focus({ preventScroll: true });
  }, []);

  const markInitialPositionComplete = useCallback(() => {
    positionedChapterIdRef.current = currentChapterIdRef.current;
    if (isInitialPositionCompleteRef.current) return;
    isInitialPositionCompleteRef.current = true;
    setIsInitialPositionComplete(true);
  }, []);

  const closeEndnotePopup = useCallback(() => {
    openEndnoteAnchorRef.current = null;
    setEndnotePopup(null);
  }, []);

  // Fetches (or reuses the cached) endnotes chapter, looks up `noteId`, and
  // opens/updates the popup in place. A monotonic request counter guards
  // against a slow response clobbering a popup opened by a later click.
  const loadEndnote = useCallback((noteId: string, noteNumber: string | null, anchorRect: DOMRect, citingChapterId: string) => {
    // Reading a note while the audiobook narrates over it is disorienting --
    // pause playback so the reader can read at their own pace.
    audioRef.current?.pause();
    const requestId = latestNoteRequestRef.current + 1;
    latestNoteRequestRef.current = requestId;
    setEndnotePopup({ noteId, noteNumber, citingChapterId, anchorRect, status: 'loading', noteElement: null });
    const endnotesChapter = readerBook?.chapters.find((chapter) => chapter.basename === ENDNOTES_BASENAME) ?? null;
    if (!readerBook || !endnotesChapter) {
      return;
    }
    endnotesIndex
      .getNote(noteId)
      .then((note) => {
        if (latestNoteRequestRef.current !== requestId) return;
        setEndnotePopup((prev) => (prev && prev.noteId === noteId ? {
          ...prev,
          status: note ? 'ready' : 'not-found',
          noteElement: note ? stripBacklink(note, endnotesChapter, citingChapterId, readerBook.chapters) : null,
        } : prev));
      })
      .catch(() => {
        if (latestNoteRequestRef.current !== requestId) return;
        setEndnotePopup((prev) => (prev && prev.noteId === noteId ? { ...prev, status: 'error', noteElement: null } : prev));
      });
  }, [audioRef, readerBook, endnotesIndex]);

  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const updateScrollbarWidth = () => {
      setScrollbarWidth(scrollContainer.offsetWidth - scrollContainer.clientWidth);
    };

    updateScrollbarWidth();
    const resizeObserver = new ResizeObserver(updateScrollbarWidth);
    resizeObserver.observe(scrollContainer);
    return () => resizeObserver.disconnect();
  }, [readerBook]);

  const updateFollowSuspended = useCallback((suspended: boolean) => {
    isFollowSuspendedRef.current = suspended;
    setIsFollowSuspended(suspended);
  }, []);

  const finishExplicitNavigation = useCallback((chapterId: string) => {
    const request = pendingChapterSelectionRequestRef.current;
    if (!request || request.chapterId !== chapterId) return;

    pendingInstantChapterIdRef.current = null;
    holdExplicitNavigationPositionRef.current = true;
    if (selectionRevealAnimationFrameRef.current !== null) {
      cancelAnimationFrame(selectionRevealAnimationFrameRef.current);
    }
    selectionRevealAnimationFrameRef.current = requestAnimationFrame(() => {
      selectionRevealAnimationFrameRef.current = null;
      if (pendingChapterSelectionRequestRef.current?.requestId !== request.requestId) return;
      pendingChapterSelectionRequestRef.current = null;
    });
  }, []);

  // A chapter chosen from the menu is an explicit navigation request. Resume
  // following before the active chapter changes so chapter positioning is not
  // mistaken for a manually suspended follow state, and remember that the
  // precise positioning pass must snap rather than animate.
  useLayoutEffect(() => {
    if (
      !chapterSelectionRequest ||
      chapterSelectionRequest.requestId === handledChapterSelectionRequestRef.current
    ) {
      return;
    }
    handledChapterSelectionRequestRef.current = chapterSelectionRequest.requestId;
    if (selectionRevealAnimationFrameRef.current !== null) {
      cancelAnimationFrame(selectionRevealAnimationFrameRef.current);
      selectionRevealAnimationFrameRef.current = null;
    }
    pendingChapterSelectionRequestRef.current = chapterSelectionRequest;
    pendingInstantChapterIdRef.current = chapterSelectionRequest.chapterId;
    const selectedChapter = readerBook?.chapters.find(({ id }) => id === chapterSelectionRequest.chapterId);
    if (selectedChapter?.title) setNavigationNotice(`Opened ${selectedChapter.title}`);
    updateFollowSuspended(false);
  }, [chapterSelectionRequest, readerBook?.chapters, updateFollowSuspended]);

  // Tapping a word seeks and starts audio playback. That must not happen
  // while the user is talking to Aleph, so keep the listener's view of the
  // voice session current and cancel any seek already in flight when one
  // starts.
  useEffect(() => {
    isVoiceSessionActiveRef.current = isVoiceSessionActive;
    if (isVoiceSessionActive) {
      latestTokenClickRequestRef.current += 1;
      inFlightTokenFetchAbortRef.current?.abort();
      inFlightTokenFetchAbortRef.current = null;
    }
  }, [isVoiceSessionActive]);

  const shouldSync =
    Boolean(currentChapter) &&
    currentChapter?.contentMode === 'standard' &&
    isRenderProfileSupported &&
    chapterTimingsStatus === 'ready';

  // Same eligibility as shouldSync, but based only on synchronous chapter
  // metadata so the coarse positioning pass knows whether precise word-sync
  // positioning is still expected.
  const isWordSyncCapableChapter =
    Boolean(currentChapter) &&
    currentChapter?.contentMode === 'standard' &&
    isRenderProfileSupported &&
    Boolean(getReaderChapterVoice(currentChapter, selectedReadingVoiceKey)?.wordSync.available);
  const isTitlePageChapter =
    currentChapter?.id === 'titlepage' ||
    currentChapter?.basename === 'titlepage';

  const handleShadowReady = useCallback(({ shadowRoot, readerRoot, popupRoot }: { shadowRoot: ShadowRoot; readerRoot: HTMLDivElement; popupRoot: HTMLDivElement }) => {
    shadowRootRef.current = shadowRoot;
    readerRootRef.current = readerRoot;
    popupRootRef.current = popupRoot;
    setShadowReadyVersion((prev) => prev + 1);
  }, []);

  const currentChapterIndex = currentChapter
    ? readerBook?.chapters.findIndex((chapter) => chapter.id === currentChapter.id) ?? -1
    : -1;
  const {
    documentVersion: htmlVersion,
    isLoading: isDocumentLoading,
    isCssReady,
    error: documentError,
    hasReadableContent,
    neighborWarning,
    indexError,
    layoutReadiness,
    retryCurrentChapter,
  } = useChapterFragments({
    bookId: readerBook?.id ?? null,
    chapters: readerBook?.chapters ?? [],
    currentChapterIndex: Math.max(0, currentChapterIndex),
    cssUrls: readerBook?.ebook.cssUrls || [],
    shadowRootRef,
    readerRootRef,
    scrollContainerRef,
    shadowReadyVersion,
    isInteractive: shouldSync && !isVoiceSessionActive,
    pendingSelectionChapterIdRef: pendingInstantChapterIdRef,
  });

  useLayoutEffect(() => {
    chapterScrollOffsetsRef.current.clear();
    previousChapterIdRef.current = null;
    positionedChapterIdRef.current = null;
    isInitialPositionCompleteRef.current = false;
    setIsInitialPositionComplete(false);
    setNavigationNotice(null);
  }, [readerBook?.id]);

  useLayoutEffect(() => {
    const previousChapterId = previousChapterIdRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (previousChapterId && previousChapterId !== currentChapter?.id && scrollContainer) {
      const previousRoot = getMountedChapterIndex(previousChapterId)?.root;
      if (previousRoot) {
        const rootRect = previousRoot.getBoundingClientRect();
        const scrollRect = scrollContainer.getBoundingClientRect();
        const chapterTop = scrollContainer.scrollTop + rootRect.top - scrollRect.top;
        chapterScrollOffsetsRef.current.set(previousChapterId, scrollContainer.scrollTop - chapterTop);
      }
    }
    previousChapterIdRef.current = currentChapter?.id ?? null;
  }, [currentChapter?.id]);

  // Position only when the document or selected chapter actually changes.
  // Opening the player is handled separately by returnToCurrentSentence, which
  // avoids two competing geometry reads and scroll operations during open.
  useLayoutEffect(() => {
    const activeChapterId = currentChapter?.id;
    if (!activeChapterId || htmlVersion <= 0) {
      return;
    }

    // `htmlVersion` advances again once the neighbours around the selected
    // chapter finish mounting, which is network-bound and routinely lands
    // after word sync has already placed the view precisely. Re-running this
    // coarse pass then would scroll an established position back to the
    // chapter heading, so once this chapter has been positioned there is
    // nothing left for it to do: the same-chapter remount preserves scroll
    // (useChapterFragments compensates for it) and word-sync following owns
    // the view from here.
    if (positionedChapterIdRef.current === activeChapterId) {
      return;
    }

    if (isFollowSuspendedRef.current) {
      if (suppressScrollRef.current) {
        suppressScrollRef.current = false;
      }
      return;
    }

    if (suppressScrollRef.current) {
      suppressScrollRef.current = false;
      return;
    }

    const readerRoot = readerRootRef.current;
    if (!readerRoot) {
      return;
    }

    const chapterRoot = resolveChapterRoot(readerRoot, activeChapterId);
    if (!chapterRoot) {
      // No distinct chapter marker in this document (common when the ebook
      // HTML contains only the current chapter) — nothing for this coarse
      // pass to do. Don't mark positioning complete here: the word-sync
      // effect below resolves the chapter root independently (with its own
      // whole-container fallback) and is still responsible for the real
      // instant snap.
      return;
    }

    const isPendingMenuSelection =
      pendingInstantChapterIdRef.current === activeChapterId;
    if (isPendingMenuSelection) {
      // Explicit selector navigation is positioned only after word-sync (or
      // the degraded heading fallback) has settled. Painting this coarse
      // top-aligned pass is the source of the visible top-then-centre jump.
      automaticScrollUntilRef.current = Date.now() + 2000;
      return;
    }
    const behavior: ScrollBehavior = isInitialPositionCompleteRef.current
      ? (isOpenRef.current ? 'smooth' : 'auto')
      : 'auto';
    automaticScrollUntilRef.current = Date.now() + 2000;

    const scrollContainer = scrollContainerRef.current;
    let positionedAtBookStart = false;
    const savedChapterScrollOffset = chapterScrollOffsetsRef.current.get(activeChapterId);
    if (savedChapterScrollOffset !== undefined && scrollContainer) {
      const chapterRect = chapterRoot.getBoundingClientRect();
      const scrollRect = scrollContainer.getBoundingClientRect();
      const chapterTopInScroll = scrollContainer.scrollTop + chapterRect.top - scrollRect.top;
      scrollContainer.scrollTo({
        top: Math.max(0, chapterTopInScroll + savedChapterScrollOffset),
        behavior,
      });
      positionedAtBookStart = true;
    }
    if (isWordSyncCapableChapter && scrollContainer && scrollContainer.clientHeight > 0) {
      const chapterRect = chapterRoot.getBoundingClientRect();
      const scrollRect = scrollContainer.getBoundingClientRect();
      const chapterTopInScroll =
        scrollContainer.scrollTop + chapterRect.top - scrollRect.top;

      // When short front matter leaves the chapter within the first half of
      // the viewport, its opening words cannot yet be centered. Aligning the
      // chapter to the top here would be immediately undone by midpoint
      // following, producing a visible up-then-down bounce.
      if (chapterTopInScroll < scrollContainer.clientHeight / 2) {
        scrollContainer.scrollTo({ top: 0, behavior });
        positionedAtBookStart = true;
      }
    }

    if (!positionedAtBookStart) {
      chapterRoot.scrollIntoView({ block: 'start', behavior });
    }
    positionedChapterIdRef.current = activeChapterId;

    // If this chapter has no word-sync data coming, this coarse scroll IS
    // the final positioning pass for it — mark it done so future opens/
    // navigations can animate normally and the reveal mask can lift.
    if (!isWordSyncCapableChapter) {
      if (isPendingMenuSelection) {
        pendingInstantChapterIdRef.current = null;
      }
      markInitialPositionComplete();
    }
  }, [currentChapter?.id, htmlVersion, isWordSyncCapableChapter, markInitialPositionComplete]);

  const canUseWordSync =
    shouldSync &&
    !isDocumentLoading &&
    isCssReady &&
    !documentError &&
    htmlVersion > 0;

  const wordSync = useWordSync({
    chapterId: currentChapter?.id || null,
    runtime,
    containerRef: readerRootRef,
    documentVersion: htmlVersion,
    enabled: canUseWordSync,
  });

  const isSyncDegraded = Boolean(
    currentChapter?.contentMode === 'standard' &&
    (
      chapterTimingsStatus === 'error' ||
      chapterTimingsStatus === 'unavailable' ||
      indexError ||
      wordSync.hasIntegrityMismatch
    )
  );
  const isSyncSettled = Boolean(
    currentChapter?.contentMode !== 'standard' ||
    isSyncDegraded ||
    (chapterTimingsStatus === 'ready' && wordSync.isIndexReady)
  );

  useEffect(() => {
    const ready = Boolean(
      currentChapter &&
      !isDocumentLoading &&
      isCssReady &&
      !documentError &&
      isInitialPositionComplete &&
      isSyncSettled
    );
    onReaderContentReadinessChange(
      ready && currentChapter
        ? { chapterId: currentChapter.id, sync: isSyncDegraded ? 'degraded' : 'ready' }
        : null,
    );
    return () => onReaderContentReadinessChange(null);
  }, [
    currentChapter,
    documentError,
    isCssReady,
    isDocumentLoading,
    isInitialPositionComplete,
    isSyncDegraded,
    isSyncSettled,
    onReaderContentReadinessChange,
  ]);

  useEffect(() => {
    if (!readerBook || currentChapterIndex < 0 || !selectedReadingVoiceKey) {
      return;
    }
    const controllers: AbortController[] = [];
    for (const index of [currentChapterIndex - 1, currentChapterIndex + 1]) {
      const chapter = readerBook.chapters[index];
      if (!chapter || chapter.contentMode !== 'standard') continue;
      const controller = new AbortController();
      controllers.push(controller);
      void fetchChapterRuntime(chapter, selectedReadingVoiceKey, controller.signal).catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          // Neighbour runtime prefetch is deliberately non-blocking.
        }
      });
    }
    return () => controllers.forEach((controller) => controller.abort());
  }, [currentChapterIndex, readerBook, selectedReadingVoiceKey]);

  useLayoutEffect(() => {
    if (isTitlePageChapter) {
      wordSync.clear();
    }
  }, [isTitlePageChapter, wordSync.clear]);

  useLayoutEffect(() => {
    if (!canUseWordSync || !currentChapter || !runtime) {
      scrollMetricsRef.current = null;
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    const chapterRoot = wordSync.getChapterRoot();
    if (!scrollContainer || !chapterRoot) {
      scrollMetricsRef.current = null;
      return;
    }

    const chapterRect = chapterRoot.getBoundingClientRect();
    const scrollRect = scrollContainer.getBoundingClientRect();
    if (scrollRect.height <= 0) {
      scrollMetricsRef.current = null;
      return;
    }

    scrollMetricsRef.current = {
      chapterTopInScroll: scrollContainer.scrollTop + chapterRect.top - scrollRect.top,
      viewportHeight: scrollRect.height,
      maxScrollTop: Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight),
    };
  }, [canUseWordSync, currentChapter, runtime, wordSync.layoutVersion, wordSync.getChapterRoot]);

  const syncToAudioTime = useCallback((options: FollowScrollOptions = {}) => {
    const forceFollow = options.forceFollow ?? false;
    const audio = audioRef.current;
    if (!audio || !canUseWordSync || !currentChapter || !runtime) {
      wordSync.clear();
      return false;
    }
    if (holdExplicitNavigationPositionRef.current && audio.paused && !forceFollow) {
      return false;
    }
    if (!audio.paused || forceFollow) holdExplicitNavigationPositionRef.current = false;

    // While a saved offset is pending the element still reads 0: its source
    // is withheld until this reader reports it has positioned itself, so the
    // restore seek always lands after the reader is already in place. Every
    // sync in that window — the first positioning pass, audio-event syncs and
    // layout-driven follows alike — must answer from the pending offset, or it
    // resolves to the chapter's first word and scrolls toward the chapter
    // start until the seek lands and snaps the view back.
    const positionSeconds = pendingResumeSecondsRef.current ?? audio.currentTime ?? 0;
    const currentMs = Math.max(0, positionSeconds * 1000);
    const timingIndex = resolveWordSyncTimingIndex(runtime.timings, currentMs);
    if (timingIndex < 0 || timingIndex >= runtime.timings.startsMs.length) {
      wordSync.clear();
      return false;
    }

    if (!isTitlePageChapter) {
      wordSync.paintTimingIndex(timingIndex);
    }

    if (isFollowSuspendedRef.current && !forceFollow) {
      return false;
    }

    const scrollContainer = scrollContainerRef.current;
    const scrollMetrics = scrollMetricsRef.current;
    if (!scrollContainer || !scrollMetrics) {
      return false;
    }

    if (!forceFollow && lastFollowTimingIndexRef.current === timingIndex) {
      return false;
    }

    const tokenRect = wordSync.getTokenRectForTimingIndex(timingIndex);
    if (!tokenRect || tokenRect.height <= 0) {
      return false;
    }

    const lineKey = getLineKey(tokenRect.top, tokenRect.height);
    if (!forceFollow && lastFollowLineKeyRef.current === lineKey) {
      lastFollowTimingIndexRef.current = timingIndex;
      return false;
    }

    const tokenCenterY = scrollMetrics.chapterTopInScroll + tokenRect.top + (tokenRect.height / 2);
    const desiredScrollTop = tokenCenterY - (scrollMetrics.viewportHeight / 2);
    const clampedScrollTop = Math.max(0, Math.min(desiredScrollTop, scrollMetrics.maxScrollTop));

    if (!forceFollow && !hasStartedMidpointFollowRef.current) {
      // At the chapter start, don't force-scroll. Begin follow once the active
      // token naturally reaches/passes midpoint and can be centered.
      if (desiredScrollTop <= 0) {
        return false;
      }
      hasStartedMidpointFollowRef.current = true;
    }

    const activeLineCenterInViewport = tokenCenterY - scrollContainer.scrollTop;
    const viewportMidpoint = scrollMetrics.viewportHeight / 2;
    const midpointDeadZone = Math.min(MAX_MIDPOINT_DEAD_ZONE_PX, tokenRect.height / 2);
    if (!forceFollow && Math.abs(activeLineCenterInViewport - viewportMidpoint) <= midpointDeadZone) {
      lastFollowTimingIndexRef.current = timingIndex;
      lastFollowLineKeyRef.current = lineKey;
      return false;
    }

    if (!forceFollow && lastFollowTimingIndexRef.current === timingIndex && Math.abs(scrollContainer.scrollTop - clampedScrollTop) < MIN_SCROLL_DELTA_PX) {
      lastFollowLineKeyRef.current = lineKey;
      return false;
    }

    if (!forceFollow && lastFollowTargetTopRef.current !== null && Math.abs(lastFollowTargetTopRef.current - clampedScrollTop) < MIN_SCROLL_DELTA_PX) {
      lastFollowTimingIndexRef.current = timingIndex;
      lastFollowLineKeyRef.current = lineKey;
      return false;
    }

    lastFollowTimingIndexRef.current = timingIndex;
    lastFollowLineKeyRef.current = lineKey;
    lastFollowTargetTopRef.current = clampedScrollTop;
    if (forceFollow) {
      hasStartedMidpointFollowRef.current = desiredScrollTop > 0;
    }
    const requestedBehavior = options.behavior ?? 'smooth';
    const scrollDistance = Math.abs(clampedScrollTop - scrollContainer.scrollTop);
    const maxSmoothDistance = options.maxSmoothDistanceViewports === undefined
      ? Number.POSITIVE_INFINITY
      : scrollMetrics.viewportHeight * options.maxSmoothDistanceViewports;
    const behavior: ScrollBehavior =
      requestedBehavior === 'smooth' && scrollDistance > maxSmoothDistance
        ? 'instant'
        : requestedBehavior;

    automaticScrollUntilRef.current = Date.now() + 2000;
    scrollContainer.scrollTo({ top: clampedScrollTop, behavior });
    return true;
  }, [
    audioRef,
    canUseWordSync,
    currentChapter,
    isTitlePageChapter,
    runtime,
    wordSync,
  ]);

  const returnToCurrentSentence = useCallback((focusTranscript: boolean) => {
    updateFollowSuspended(false);
    lastFollowTimingIndexRef.current = -1;
    lastFollowLineKeyRef.current = null;
    lastFollowTargetTopRef.current = null;
    const didPosition = syncToAudioTime({
      behavior: focusTranscript ? 'smooth' : 'auto',
      forceFollow: true,
      maxSmoothDistanceViewports: focusTranscript
        ? MAX_RETURN_SMOOTH_DISTANCE_VIEWPORTS
        : undefined,
    });

    if (focusTranscript) {
      scrollContainerRef.current?.focus({ preventScroll: true });
    }
    return didPosition;
  }, [syncToAudioTime, updateFollowSuspended]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !canUseWordSync || !isOpen) {
      return;
    }

    let hasPendingManualScrollIntent = false;
    let isDraggingScrollbar = false;
    let intentTimeoutId: number | null = null;
    let previousScrollTop = scrollContainer.scrollTop;

    const clearManualScrollIntent = () => {
      hasPendingManualScrollIntent = false;
      if (intentTimeoutId !== null) {
        window.clearTimeout(intentTimeoutId);
        intentTimeoutId = null;
      }
    };

    const markManualScrollIntent = () => {
      hasPendingManualScrollIntent = true;
      if (intentTimeoutId !== null) {
        window.clearTimeout(intentTimeoutId);
      }
      intentTimeoutId = window.setTimeout(clearManualScrollIntent, MANUAL_SCROLL_INTENT_TIMEOUT_MS);
    };

    const handleWheel = (event: WheelEvent) => {
      if (event.deltaY !== 0) {
        markManualScrollIntent();
      }
    };

    const handleTouchMove = () => {
      markManualScrollIntent();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (SCROLL_KEYS.has(event.key)) {
        markManualScrollIntent();
      }
    };

    const beginsInScrollbar = (clientX: number) => {
      const scrollbarWidth = scrollContainer.offsetWidth - scrollContainer.clientWidth;
      if (scrollbarWidth <= 0) {
        return false;
      }

      const rect = scrollContainer.getBoundingClientRect();
      return clientX >= rect.right - scrollbarWidth;
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && beginsInScrollbar(event.clientX)) {
        isDraggingScrollbar = true;
        markManualScrollIntent();
      }
    };

    // Chromium/WebKit handle native scrollbar interaction outside the normal
    // pointer-event path, but still dispatch a mouse event to the scroller.
    const handleMouseDown = (event: MouseEvent) => {
      if (beginsInScrollbar(event.clientX)) {
        isDraggingScrollbar = true;
        markManualScrollIntent();
      }
    };

    const handlePointerEnd = () => {
      isDraggingScrollbar = false;
    };

    const handleScroll = () => {
      const hasManualIntent = hasPendingManualScrollIntent || isDraggingScrollbar;
      const now = Date.now();
      const hasInFlightAutomaticScroll =
        automaticScrollUntilRef.current > 0 &&
        now <= automaticScrollUntilRef.current;
      if (!hasManualIntent && hasInFlightAutomaticScroll) {
        // Do not read scrollTop during a recognized automatic scroll. If
        // another part of the page has invalidated layout, that read forces
        // Chrome to synchronously lay out the entire reader on every tick.
        automaticScrollUntilRef.current = now + AUTOMATIC_SCROLL_GRACE_MS;
        return;
      }

      const nextScrollTop = scrollContainer.scrollTop;
      const hasMoved = Math.abs(nextScrollTop - previousScrollTop) >= MIN_SCROLL_DELTA_PX;
      previousScrollTop = nextScrollTop;

      // A scroll event alone is not proof of user intent. Mobile browsers can
      // adjust scrollTop while fonts, deferred content, scroll anchoring, and
      // the visual viewport settle. Treat those changes as a new baseline
      // without suspending follow mode.
      if (!hasManualIntent || !hasMoved) {
        return;
      }

      clearManualScrollIntent();
      automaticScrollUntilRef.current = 0;
      updateFollowSuspended(true);
      // Only stop an in-flight smooth follow animation when we know one is
      // actually running. Calling scrollTo unconditionally here — even "to the
      // same position" — can abort native touch-momentum scrolling that's
      // already in progress and has nothing to do with us, which is exactly
      // what leaves the transcript stuck after a single short drag on mobile.
      if (hasInFlightAutomaticScroll) {
        scrollContainer.scrollTo({ top: nextScrollTop, behavior: 'auto' });
      }
    };

    const handleScrollEnd = () => {
      previousScrollTop = scrollContainer.scrollTop;
      // A delayed scrollend may belong to an older programmatic scroll. Leave
      // the rolling grace deadline intact so it cannot disarm a newer follow
      // scroll; explicit manual intent bypasses that protection immediately.
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: true });
    scrollContainer.addEventListener('touchmove', handleTouchMove, { passive: true });
    scrollContainer.addEventListener('keydown', handleKeyDown);
    scrollContainer.addEventListener('pointerdown', handlePointerDown);
    scrollContainer.addEventListener('mousedown', handleMouseDown);
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    scrollContainer.addEventListener('scrollend', handleScrollEnd);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);
    window.addEventListener('mouseup', handlePointerEnd);

    return () => {
      clearManualScrollIntent();
      scrollContainer.removeEventListener('wheel', handleWheel);
      scrollContainer.removeEventListener('touchmove', handleTouchMove);
      scrollContainer.removeEventListener('keydown', handleKeyDown);
      scrollContainer.removeEventListener('pointerdown', handlePointerDown);
      scrollContainer.removeEventListener('mousedown', handleMouseDown);
      scrollContainer.removeEventListener('scroll', handleScroll);
      scrollContainer.removeEventListener('scrollend', handleScrollEnd);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
      window.removeEventListener('mouseup', handlePointerEnd);
    };
  }, [canUseWordSync, isOpen, updateFollowSuspended]);

  useEffect(() => {
    const wasOpen = previousIsOpenRef.current;
    previousIsOpenRef.current = isOpen;
    if (isOpen && !wasOpen) {
      returnToCurrentSentence(false);
    }
  }, [isOpen, returnToCurrentSentence]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !canUseWordSync) {
      wordSync.clear();
      return;
    }

    const stopSyncLoop = () => {
      if (syncAnimationFrameRef.current !== null) {
        cancelAnimationFrame(syncAnimationFrameRef.current);
        syncAnimationFrameRef.current = null;
      }
    };

    const tick = () => {
      // Skip continuous highlight/follow work while the reader is
      // closed. Its layout remains available for the one-time reopen sync.
      if (isOpen) {
        syncToAudioTime();
      }
      if (!audio.paused && !audio.ended) {
        syncAnimationFrameRef.current = requestAnimationFrame(tick);
      } else {
        syncAnimationFrameRef.current = null;
      }
    };

    const startSyncLoop = () => {
      if (syncAnimationFrameRef.current !== null) {
        return;
      }
      syncAnimationFrameRef.current = requestAnimationFrame(tick);
    };

    const syncOnce = () => {
      if (isOpen) {
        syncToAudioTime();
      }
    };

    syncOnce();
    if (!audio.paused && !audio.ended) {
      startSyncLoop();
    }

    audio.addEventListener('play', startSyncLoop);
    audio.addEventListener('playing', startSyncLoop);
    audio.addEventListener('pause', syncOnce);
    audio.addEventListener('seeked', syncOnce);
    audio.addEventListener('timeupdate', syncOnce);
    audio.addEventListener('ended', syncOnce);

    return () => {
      stopSyncLoop();
      audio.removeEventListener('play', startSyncLoop);
      audio.removeEventListener('playing', startSyncLoop);
      audio.removeEventListener('pause', syncOnce);
      audio.removeEventListener('seeked', syncOnce);
      audio.removeEventListener('timeupdate', syncOnce);
      audio.removeEventListener('ended', syncOnce);
    };
  }, [audioRef, canUseWordSync, isOpen, syncToAudioTime, wordSync]);

  useLayoutEffect(() => {
    hasStartedMidpointFollowRef.current = false;
    lastFollowTimingIndexRef.current = -1;
    lastFollowLineKeyRef.current = null;
    lastFollowTargetTopRef.current = null;
    const isPendingMenuSelection =
      pendingInstantChapterIdRef.current === currentChapter?.id;
    const isEndnotesChapter = currentChapter?.basename === ENDNOTES_BASENAME;

    if (isPendingMenuSelection && documentError) {
      finishExplicitNavigation(currentChapter!.id);
      return;
    }

    if (isPendingMenuSelection) {
      const isDocumentReady =
        !isDocumentLoading &&
        isCssReady &&
        htmlVersion > 0 &&
        layoutReadiness?.chapterId === currentChapter!.id &&
        layoutReadiness.status === 'settled';
      if (!isDocumentReady) return;

      const readerRoot = readerRootRef.current;
      const scrollContainer = scrollContainerRef.current;
      // If this menu selection targets a specific anchor (e.g. an endnote
      // backlink), snap straight there instead of the chapter heading, so the
      // later fragment-aware positionRequest pass below doesn't visibly
      // re-scroll from the heading to the anchor.
      const pendingFragmentId = pendingChapterSelectionRequestRef.current?.fragmentId ?? null;
      let revealed = false;
      if (pendingFragmentId && scrollContainer) {
        const mountedTarget = getMountedChapterIndex(currentChapter!.id);
        if (mountedTarget) {
          suppressScrollRef.current = true;
          automaticScrollUntilRef.current = Date.now() + 2000;
          const target = revealNavigationTarget(mountedTarget.root, pendingFragmentId, scrollContainer, 'instant');
          if (target) {
            focusExplicitNavigationTarget(target);
            revealed = true;
          }
        }
      }
      if (!revealed && readerRoot && scrollContainer) centerChapterOpening(readerRoot, scrollContainer, currentChapter!.id, isEndnotesChapter);
      finishExplicitNavigation(currentChapter!.id);
      markInitialPositionComplete();
      return;
    }

    if (!isInitialPositionCompleteRef.current) {
      const isDocumentReady =
        !isDocumentLoading &&
        isCssReady &&
        !documentError &&
        htmlVersion > 0;
      if (!isDocumentReady || chapterTimingsStatus === 'loading') {
        return;
      }

      if (
        !isWordSyncCapableChapter ||
        chapterTimingsStatus === 'unavailable' ||
        chapterTimingsStatus === 'error' ||
        indexError ||
        wordSync.hasIntegrityMismatch
      ) {
        if (isPendingMenuSelection) {
          const readerRoot = readerRootRef.current;
          const scrollContainer = scrollContainerRef.current;
          if (readerRoot && scrollContainer) centerChapterOpening(readerRoot, scrollContainer, currentChapter!.id, isEndnotesChapter);
          finishExplicitNavigation(currentChapter!.id);
        }
        markInitialPositionComplete();
        return;
      }

      if (!canUseWordSync || !wordSync.isIndexReady) {
        return;
      }

      // First-ever positioning pass for this mounted reader instance: snap
      // straight to the correct line instead of animating from wherever the
      // coarse chapter-level scrollIntoView pass left off. A successful token
      // measurement and scroll is the readiness signal; no elapsed-time guess
      // is involved.
      const didPosition = isPendingMenuSelection
        ? syncToAudioTime({ behavior: 'instant', forceFollow: true })
        : returnToCurrentSentence(false);
      if (didPosition) {
        if (isPendingMenuSelection) {
          finishExplicitNavigation(currentChapter!.id);
        }
        markInitialPositionComplete();
        return;
      }

      console.warn(
        `[reader] Could not position word sync for chapter '${currentChapter?.id ?? 'unknown'}'. ` +
          'Continuing from the chapter fallback position.'
      );
      if (isPendingMenuSelection) {
        const readerRoot = readerRootRef.current;
        const scrollContainer = scrollContainerRef.current;
        if (readerRoot && scrollContainer) centerChapterOpening(readerRoot, scrollContainer, currentChapter!.id, isEndnotesChapter);
        finishExplicitNavigation(currentChapter!.id);
      }
      markInitialPositionComplete();
      return;
    }

    syncToAudioTime();
  }, [
    currentChapter?.id,
    htmlVersion,
    canUseWordSync,
    chapterTimingsStatus,
    isDocumentLoading,
    isCssReady,
    layoutReadiness,
    documentError,
    indexError,
    wordSync.isIndexReady,
    wordSync.hasIntegrityMismatch,
    wordSync.layoutVersion,
    isWordSyncCapableChapter,
    syncToAudioTime,
    returnToCurrentSentence,
    markInitialPositionComplete,
    finishExplicitNavigation,
    focusExplicitNavigationTarget,
  ]);

  useLayoutEffect(() => {
    if (!positionRequest || positionRequest.chapterId !== currentChapter?.id) {
      return;
    }
    if (acknowledgedPositionRequestRef.current === positionRequest.requestId) {
      return;
    }
    const isDocumentReady =
      !isDocumentLoading &&
      isCssReady &&
      !documentError &&
      htmlVersion > 0;
    if (!isDocumentReady || chapterTimingsStatus === 'loading' || !isSyncSettled) {
      return;
    }
    if (!isSyncDegraded && (!canUseWordSync || !wordSync.isIndexReady)) {
      return;
    }

    if (positionRequest.mode === 'chapter-heading') {
      let revealed = false;
      if (positionRequest.fragmentId) {
        const mountedTarget = getMountedChapterIndex(positionRequest.chapterId);
        if (mountedTarget) {
          suppressScrollRef.current = true;
          automaticScrollUntilRef.current = Date.now() + 2000;
          const target = revealNavigationTarget(mountedTarget.root, positionRequest.fragmentId, scrollContainerRef.current);
          if (target) {
            focusExplicitNavigationTarget(target);
            revealed = true;
          }
          setNavigationNotice(
            target
              ? `Opened ${currentChapter?.title ?? 'chapter'} at ${positionRequest.fragmentId}`
              : `The requested location '${positionRequest.fragmentId}' was not found in ${currentChapter?.title ?? 'the chapter'}.`,
          );
        }
      }
      if (!revealed) {
        const readerRoot = readerRootRef.current;
        const scrollContainer = scrollContainerRef.current;
        if (readerRoot && scrollContainer) centerChapterOpening(readerRoot, scrollContainer, positionRequest.chapterId, currentChapter?.basename === ENDNOTES_BASENAME);
      }
      holdExplicitNavigationPositionRef.current = true;
    } else if (!isSyncDegraded) {
      syncToAudioTime({ behavior: 'instant', forceFollow: true });
    }
    markInitialPositionComplete();
    acknowledgedPositionRequestRef.current = positionRequest.requestId;
    onReaderPositioned({
      chapterId: positionRequest.chapterId,
      requestId: positionRequest.requestId,
      mode: positionRequest.mode,
      fragmentId: positionRequest.fragmentId ?? null,
    });
  }, [
    canUseWordSync,
    chapterTimingsStatus,
    currentChapter?.id,
    currentChapter?.title,
    currentChapter?.basename,
    documentError,
    focusExplicitNavigationTarget,
    htmlVersion,
    isCssReady,
    isDocumentLoading,
    isSyncDegraded,
    isSyncSettled,
    markInitialPositionComplete,
    onReaderPositioned,
    positionRequest,
    syncToAudioTime,
    wordSync.isIndexReady,
  ]);

  useEffect(() => () => onReaderPositioned(null), [onReaderPositioned]);

  useEffect(() => () => {
    if (syncAnimationFrameRef.current !== null) {
      cancelAnimationFrame(syncAnimationFrameRef.current);
      syncAnimationFrameRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    if (selectionRevealAnimationFrameRef.current !== null) {
      cancelAnimationFrame(selectionRevealAnimationFrameRef.current);
    }
  }, []);

  useEffect(() => {
    const container = readerRootRef.current;
    const shadowRoot = shadowRootRef.current;
    const currentChapterId = currentChapter?.id || null;
    const isDocumentReady = !isDocumentLoading && !documentError && htmlVersion > 0;
    if (!container || !shadowRoot || !isRenderProfileSupported || !readerBook || !isDocumentReady) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      pointerGestureRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
    };
    const handlePointerMove = (event: PointerEvent) => {
      const gesture = pointerGestureRef.current;
      if (gesture && gesture.pointerId === event.pointerId && Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 8) {
        gesture.moved = true;
      }
    };
    const handlePointerCancel = (event: PointerEvent) => {
      if (pointerGestureRef.current?.pointerId === event.pointerId) {
        pointerGestureRef.current = null;
      }
    };
    const handlePointerUp = (event: PointerEvent) => {
      const gesture = pointerGestureRef.current;
      if (!gesture || gesture.pointerId !== event.pointerId) {
        return;
      }
      if (!gesture.moved) {
        // No drag happened, so there's nothing for the upcoming click to
        // suppress -- clear now rather than let it linger.
        pointerGestureRef.current = null;
        return;
      }
      // A drag ended: keep `moved` around just long enough for the paired
      // synthetic click (handleHostClick) to consume it and suppress itself.
      // If no click follows -- e.g. the pointer was released off-target --
      // fall back to clearing on the next tick so a later, unrelated
      // keyboard-triggered click (no pointerId of its own) isn't wrongly
      // suppressed by stale drag state.
      const { pointerId } = gesture;
      setTimeout(() => {
        if (pointerGestureRef.current?.pointerId === pointerId) {
          pointerGestureRef.current = null;
        }
      }, 0);
    };

    const handleHostClick = async (event: MouseEvent) => {
      if (pointerGestureRef.current?.moved) {
        pointerGestureRef.current = null;
        return;
      }
      pointerGestureRef.current = null;
      const hitTarget = event.composedPath().find((node): node is HTMLElement => node instanceof HTMLElement);
      if (!(hitTarget instanceof HTMLElement)) {
        return;
      }

      // Any click reaching here originates in the reader content (not the
      // popup itself, which is mounted in a separate shadow-root slot) --
      // close a stale popup up front. If this click turns out to target a
      // footnote link, the branch below reopens/updates it in the same tick,
      // so React's batching means there's no visible close-then-reopen flash.
      // The anchor that opened the current popup (if any) is captured before
      // closing so the footnote branch below can tell a re-click of the same
      // link apart from a click that opens a different note.
      const previouslyOpenAnchor = openEndnoteAnchorRef.current;
      closeEndnotePopup();

      const ownerChapter = readerBook.chapters.find((chapter) =>
        getMountedChapterIndex(chapter.id)?.root.contains(hitTarget)
      ) ?? null;
      const ownerRoot = ownerChapter ? getMountedChapterIndex(ownerChapter.id)?.root ?? null : null;
      if (!ownerChapter || !ownerRoot) {
        return;
      }

      const anchor = hitTarget.closest<HTMLAnchorElement>('a[href],a[data-chapter-id],a[data-chapter-path]');
      if (anchor && ownerRoot.contains(anchor)) {
        const linkTarget = resolveReaderLink(anchor, ownerChapter, readerBook.chapters);
        if (linkTarget) {
          event.preventDefault();
          const endnotesChapterId = readerBook.chapters.find((chapter) => chapter.basename === ENDNOTES_BASENAME)?.id ?? null;
          if (endnotesChapterId && linkTarget.chapterId === endnotesChapterId && linkTarget.fragmentId) {
            if (previouslyOpenAnchor === anchor) {
              // The popup was already showing this same link's note -- treat
              // the click as a toggle-close rather than reopening it.
              return;
            }
            openEndnoteAnchorRef.current = anchor;
            loadEndnote(linkTarget.fragmentId, anchor.textContent?.trim() || null, anchor.getBoundingClientRect(), ownerChapter.id);
            return;
          }
          const mountedTarget = getMountedChapterIndex(linkTarget.chapterId);
          if (linkTarget.chapterId === currentChapterId && mountedTarget) {
            const target = revealNavigationTarget(mountedTarget.root, linkTarget.fragmentId, scrollContainerRef.current);
            if (target) focusExplicitNavigationTarget(target);
            holdExplicitNavigationPositionRef.current = true;
            setNavigationNotice(
              target
                ? `Opened ${ownerChapter.title}${linkTarget.fragmentId ? ` at ${linkTarget.fragmentId}` : ''}`
                : `The requested location '${linkTarget.fragmentId}' was not found in ${ownerChapter.title}.`,
            );
          } else {
            onNavigateChapter(linkTarget.chapterId, { fragmentId: linkTarget.fragmentId });
          }
          return;
        }
      }

      const token = hitTarget.closest<HTMLElement>('[data-w],[data-w-part]');
      if (!token || !ownerRoot.contains(token)) {
        return;
      }

      // Tap-to-seek resumes book playback, which would talk over a live voice
      // conversation. Links and endnotes above stay usable; only the seek is
      // suppressed.
      if (isVoiceSessionActiveRef.current) {
        return;
      }

      const mountedIndex = getMountedChapterIndex(ownerChapter.id);
      const ordinal = mountedIndex?.ordinalByElement.get(token);
      if (!mountedIndex || ordinal === undefined) {
        return;
      }

      const requestId = latestTokenClickRequestRef.current + 1;
      latestTokenClickRequestRef.current = requestId;
      inFlightTokenFetchAbortRef.current?.abort();
      const abortController = new AbortController();
      inFlightTokenFetchAbortRef.current = abortController;
      try {
        const targetRuntime = ownerChapter.id === currentChapterId && runtime
          ? runtime
          : await fetchChapterRuntime(ownerChapter, selectedReadingVoiceKey, abortController.signal);
        if (
          latestTokenClickRequestRef.current !== requestId ||
          abortController.signal.aborted ||
          isVoiceSessionActiveRef.current
        ) {
          return;
        }
        if (
          mountedIndex.segmentsByOrdinal.length !== targetRuntime.tokenMap.tokens.length ||
          targetRuntime.timings.startsMs.length !== targetRuntime.tokenMap.tokens.length
        ) {
          return;
        }
        const startMs = targetRuntime.timings.startsMs[ordinal];
        if (typeof startMs !== 'number') return;
        suppressScrollRef.current = true;
        automaticScrollUntilRef.current = Date.now() + 2000;
        token.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        onSeekAndPlay(startMs / 1000, ownerChapter.id);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        console.warn(`[reader] Could not seek chapter '${ownerChapter.id}' by ordinal.`, error);
      } finally {
        if (inFlightTokenFetchAbortRef.current === abortController) {
          inFlightTokenFetchAbortRef.current = null;
        }
      }
    };

    container.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointercancel', handlePointerCancel);
    window.addEventListener('pointerup', handlePointerUp);
    container.addEventListener('click', handleHostClick);
    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointercancel', handlePointerCancel);
      window.removeEventListener('pointerup', handlePointerUp);
      container.removeEventListener('click', handleHostClick);
      pointerGestureRef.current = null;
      latestTokenClickRequestRef.current += 1;
      if (inFlightTokenFetchAbortRef.current) {
        inFlightTokenFetchAbortRef.current.abort();
        inFlightTokenFetchAbortRef.current = null;
      }
    };
  }, [
    readerRootRef,
    shadowRootRef,
    currentChapter?.id,
    closeEndnotePopup,
    focusExplicitNavigationTarget,
    loadEndnote,
    onNavigateChapter,
    onSeekAndPlay,
    readerBook,
    isRenderProfileSupported,
    isDocumentLoading,
    documentError,
    htmlVersion,
    isOpen,
    runtime,
    selectedReadingVoiceKey,
  ]);

  useEffect(() => () => navigationFocusCleanupRef.current?.(), []);

  useEffect(() => {
    closeEndnotePopup();
  }, [currentChapter?.id, closeEndnotePopup]);

  const isEndnotePopupOpen = Boolean(endnotePopup);
  useEffect(() => {
    if (!isEndnotePopupOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeEndnotePopup();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isEndnotePopupOpen, closeEndnotePopup]);

  // Dismiss on a click anywhere outside the reader entirely (e.g. player
  // controls). A click landing inside the shadow host -- on the popup or on
  // reader content -- is left to the shadow-internal click handling above,
  // since a closed shadow root hides its internal targets from this
  // document-level listener's composedPath() and only exposes the host.
  useEffect(() => {
    if (!isEndnotePopupOpen) return;
    const onDocumentClick = (event: MouseEvent) => {
      const hostElement = shadowRootRef.current?.host ?? null;
      if (!hostElement || !event.composedPath().includes(hostElement)) {
        closeEndnotePopup();
      }
    };
    window.addEventListener('click', onDocumentClick);
    return () => window.removeEventListener('click', onDocumentClick);
  }, [isEndnotePopupOpen, closeEndnotePopup]);

  if (!readerBook) {
    return null;
  }

  const isLoading = !documentError && (isDocumentLoading || !isCssReady || !isInitialPositionComplete);
  const hasVisibleReadableContent = hasReadableContent && isCssReady;
  const hasSyncNotice = isSyncDegraded;
  // Offline, on a chapter whose narration never reached the device: the one
  // moment the offline feature speaks. The text below still renders.
  const atOfflineBoundary = useOfflineBoundary({ chapter: currentChapter, voiceKey: selectedReadingVoiceKey });
  const gradientStartColor = isDarkMode
    ? 'var(--background-colour-dark-mode)'
    : 'var(--background-colour-light-mode)';

  return (
    <div className={`h-full w-full min-h-0 overflow-visible ${isDarkMode ? 'transcript-scrollbar-dark' : 'transcript-scrollbar-light'}`}>
      {currentChapter && currentChapter.contentMode !== 'standard' && (
        <div className="px-4 mb-3 text-xs opacity-70">Text-only chapter</div>
      )}
      {chapterRecoveryNotice && <div className="px-4 mb-3 text-xs opacity-70" role="status">{chapterRecoveryNotice}</div>}
      {atOfflineBoundary && (
        <div className="px-4 mb-3 text-sm opacity-80" role="status" data-testid="offline-boundary">
          {OFFLINE_BOUNDARY_MESSAGE}
        </div>
      )}
      {hasSyncNotice && (
        <div className="px-4 mb-3 text-xs opacity-70" role="status">
          Word highlighting is unavailable for this chapter; audio and text remain available.
        </div>
      )}
      <div className="relative h-full">
        {navigationNotice && <div className="sr-only" role="status" aria-live="polite">{navigationNotice}</div>}
        {neighborWarning && <div className="sr-only" role="status" aria-live="polite">{neighborWarning}</div>}
        <EndnotePopup
          isOpen={Boolean(endnotePopup)}
          status={endnotePopup?.status ?? 'loading'}
          noteElement={endnotePopup?.noteElement ?? null}
          noteNumber={endnotePopup?.noteNumber ?? null}
          anchorRect={endnotePopup?.anchorRect ?? null}
          portalTarget={popupRootRef.current}
          onClose={closeEndnotePopup}
          onRetry={() => {
            if (!endnotePopup) return;
            endnotesIndex.retry();
            loadEndnote(endnotePopup.noteId, endnotePopup.noteNumber, endnotePopup.anchorRect, endnotePopup.citingChapterId);
          }}
          titleId={ENDNOTE_POPUP_TITLE_ID}
        />
        <div
          ref={scrollContainerRef}
          className="transcript-scroll-container h-full overflow-y-auto px-4 transcript-scrollbar"
          style={{ touchAction: 'pan-y' }}
          tabIndex={0}
        >
          {/* The margin dots live in the 32px the text already keeps clear
              (this padding plus the scroll container's); no gutter is reserved,
              so a book without conversations loses no reading width. */}
          <div ref={readerWrapperRef} className="w-full max-w-160 mx-auto px-4 relative min-h-30">
            <ReaderShadowHost isDarkMode={isDarkMode} onReady={handleShadowReady} />
            {onOpenConversation && (
              <ConversationMarginDots
                sessions={conversationSessions}
                chapters={readerBook?.chapters ?? NO_CHAPTERS}
                currentChapter={currentChapter}
                runtime={runtime}
                selectedReadingVoiceKey={selectedReadingVoiceKey}
                documentVersion={htmlVersion}
                layoutVersion={wordSync.layoutVersion}
                wrapperRef={readerWrapperRef}
                activeConversationId={activeConversationId}
                isDarkMode={isDarkMode}
                onOpenConversation={onOpenConversation}
              />
            )}
          </div>
        </div>
        {isLoading && !hasVisibleReadableContent && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center"
            style={{ backgroundColor: gradientStartColor }}
            role="status"
            aria-live="polite"
          >
            <div
              className="h-6 w-6 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"
              aria-hidden="true"
            />
            <span className="sr-only">Loading ebook</span>
          </div>
        )}
        {isLoading && hasVisibleReadableContent && (
          <div className="absolute top-2 inset-x-0 z-30 flex justify-center pointer-events-none" role="status">
            <span className="rounded-full border bg-white/90 px-3 py-1 text-xs text-gray-800 shadow">Loading chapter…</span>
          </div>
        )}
        {documentError && !hasVisibleReadableContent && (
          <div className="absolute inset-0 z-20 flex flex-col gap-3 items-center justify-center p-4" role="alert">
            <p className="text-sm opacity-70">Reader unavailable: {documentError}</p>
            <button type="button" className="rounded-full border px-4 py-2 text-sm" onClick={retryCurrentChapter}>
              Retry chapter
            </button>
          </div>
        )}
        {documentError && hasVisibleReadableContent && (
          <div className="absolute top-2 inset-x-0 z-30 flex justify-center px-4" role="alert">
            <div className="flex items-center gap-3 rounded-lg border bg-white/95 p-3 text-sm text-gray-800 shadow">
              <span>Reader unavailable: {documentError}</span>
              <button type="button" className="rounded-full border px-3 py-1" onClick={retryCurrentChapter}>Retry chapter</button>
            </div>
          </div>
        )}
        <div
          className="pointer-events-none absolute left-0 top-0 h-5 z-10"
          style={{
            right: scrollbarWidth,
            backgroundImage: `linear-gradient(to bottom, ${gradientStartColor}, transparent)`,
          }}
        />
        <div
          className="pointer-events-none absolute left-0 bottom-0 h-5 z-10"
          style={{
            right: scrollbarWidth,
            backgroundImage: `linear-gradient(to top, ${gradientStartColor}, transparent)`,
          }}
        />
        {isFollowSuspended && canUseWordSync && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-40 flex justify-center px-4">
            <button
              type="button"
              onClick={() => returnToCurrentSentence(true)}
              className={`pointer-events-auto cursor-pointer rounded-full border px-4 py-2 mb-2.5 text-sm font-medium shadow-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${
                isDarkMode
                  ? 'border-black-700 bg-black-900 text-white-custom hover:bg-black-800'
                  : 'border-blue-500 bg-blue-700 text-white hover:bg-blue-600'
              }`}
            >
              Return to current sentence
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const TranscribePlayback = React.memo(TranscribePlaybackComponent);

export default TranscribePlayback;
